// Receipt shown immediately after a bill is completed on the billing page.
//
// Before this, the only confirmation of a sale was the string "Bill completed
// successfully" — and since the cart is cleared a line earlier, the bill number,
// total and change due all vanished from the screen the moment they were needed.
// A receipt only ever reached the customer when a thermal printer happened to be
// connected AND auto-print was enabled.
//
// The bill passed in is the `completeBill` response (not the pre-completion
// draft), so payment_method / amount_paid / change_due and any post-discount
// totals are the real settled values.

import { useCallback, useState } from 'react';
import { Button, ReceiptPreview, GLASS, cn } from '@bbpos/ui';
import type { BillWithItems } from '@/services/billing.service';
import { billingService } from '@/services/billing.service';
import { usePrinterStore } from '@/stores/use-printer-store';
import { useTenantStore } from '@/stores/use-tenant-store';
import { useLocationStore } from '@/stores/use-location-store';
import { useTenantCurrency } from '@/hooks/use-tenant-currency';
import { useTaxRegime } from '@/hooks/use-tax-regime';
import { buildReceiptData } from '@/lib/receipt-data';
import { getLocationTimezone } from '@/lib/tenant-clock';

export interface PostSaleReceiptModalProps {
  bill: BillWithItems;
  onClose: () => void;
  /**
   * T19: a bill saved offline. It carries the till's OFL number and has no
   * server copy yet, so there is no PDF — the thermal printer prints it.
   */
  offline?: boolean;
}

export function PostSaleReceiptModal({ bill, onClose, offline = false }: PostSaleReceiptModalProps) {
  const [isDownloading, setIsDownloading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [printed, setPrinted] = useState(false);

  const tenant = useTenantStore((s) => s.tenant);
  const currentLocation = useLocationStore((s) => s.currentLocation);
  const currency = useTenantCurrency();
  const tax = useTaxRegime();

  const printerStatus = usePrinterStore((s) => s.connectionStatus);
  const printerType = usePrinterStore((s) => s.connectionType);
  const paperWidth = usePrinterStore((s) => s.paperWidth);
  const printReceipt = usePrinterStore((s) => s.printReceipt);

  // Same gate every other print call site uses: the 'browser' transport can't
  // render ESC/POS bytes, so it is treated as "no printer" and falls back to PDF.
  const isPrinterReady = printerStatus === 'connected' && printerType !== 'browser';

  const handleDownload = useCallback(async () => {
    setIsDownloading(true);
    setError(null);
    try {
      const format = paperWidth === 58 ? '58mm' : '80mm';
      const blob = await billingService.getReceipt(bill.id, format);
      const url = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = `${bill.bill_number}-receipt-${format}.pdf`;
      document.body.appendChild(a);
      a.click();
      document.body.removeChild(a);
      setTimeout(() => URL.revokeObjectURL(url), 1000);
    } catch {
      setError('Could not download the receipt PDF.');
    } finally {
      setIsDownloading(false);
    }
  }, [bill.id, bill.bill_number, paperWidth]);

  const handlePrint = useCallback(() => {
    if (!isPrinterReady && offline) {
      setError('Connect a receipt printer to print offline. The PDF is ready once the bill syncs.');
      return;
    }
    if (!isPrinterReady) {
      // No thermal printer — the PDF is the only way to get paper in hand.
      void handleDownload();
      return;
    }
    const data = buildReceiptData(bill, tenant, currentLocation);
    if (!data) {
      setError('Receipt details unavailable — try downloading the PDF.');
      return;
    }
    setError(null);
    printReceipt(data.billData, data.tenantData, data.locationData, {
      taxName: tax.taxName,
      taxIdLabel: tax.taxIdLabel,
      splitLabels: tax.splitLabels,
      showHsn: tax.showHsn,
    });
    // The print queue is fire-and-forget, so this confirms the job was sent,
    // not that paper came out.
    setPrinted(true);
    setTimeout(() => setPrinted(false), 3000);
  }, [isPrinterReady, offline, handleDownload, bill, tenant, currentLocation, printReceipt, tax]);

  return (
    <div
      role="dialog"
      aria-modal="true"
      aria-label={`Receipt ${bill.bill_number}`}
      className="fixed inset-0 z-50 flex items-center justify-center bg-neutral-950/60 backdrop-blur-sm p-4 animate-fade-in"
      onClick={onClose}
    >
      <div
        className={cn(GLASS, 'max-w-md w-full p-6 space-y-4 animate-scale-in max-h-[92vh] overflow-y-auto')}
        onClick={(e) => e.stopPropagation()}
      >
        <div className="flex items-start justify-between gap-2">
          <div>
            <h2 className="text-lg font-bold text-neutral-900 dark:text-neutral-100">
              {offline ? 'Saved offline' : 'Sale complete'}
            </h2>
            <p className="text-xs text-neutral-500 dark:text-neutral-400 font-mono">
              {bill.bill_number}
            </p>
            {offline && (
              <p className="mt-1 inline-flex items-center gap-1 rounded-full bg-warning-100 dark:bg-warning-900/30 px-2 py-0.5 text-[11px] font-medium text-warning-700 dark:text-warning-300">
                Temporary number — the final one arrives when it syncs
              </p>
            )}
          </div>
          <button
            type="button"
            onClick={onClose}
            className="rounded-lg p-1 text-neutral-400 hover:bg-neutral-100 dark:hover:bg-neutral-800"
            aria-label="Close"
          >
            <svg className="h-5 w-5" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}><path strokeLinecap="round" strokeLinejoin="round" d="M6 18L18 6M6 6l12 12" /></svg>
          </button>
        </div>

        <div className="flex justify-center rounded-xl bg-neutral-100 dark:bg-neutral-800 p-3">
          <ReceiptPreview
            bill={bill}
            items={bill.items}
            tenant={{
              name: tenant?.business_name ?? '',
              address: [currentLocation?.address, currentLocation?.city]
                .filter(Boolean)
                .join(', '),
              gstin: tenant?.show_gstin_on_receipt === false ? undefined : tenant?.gstin ?? undefined,
              phone: tenant?.phone ?? currentLocation?.phone ?? '',
              footer: tenant?.receipt_footer ?? undefined,
            }}
            variant={paperWidth === 58 ? '58mm' : '80mm'}
            currency={currency}
            timeZone={getLocationTimezone(bill.location_id)}
            isOffline={offline}
            showPreview
            // This modal has its own Print / Download / Close bar.
            showPrintControl={false}
          />
        </div>

        {error && (
          <p className="text-xs text-danger-600 dark:text-danger-400" role="alert">{error}</p>
        )}
        {printed && (
          <p className="text-xs text-success-600 dark:text-success-400" role="status">
            Sent to printer.
          </p>
        )}

        <div className="flex items-center justify-end gap-2 pt-1">
          <Button variant="ghost" onClick={onClose}>Close</Button>
          {!offline && (
            <Button variant="secondary" onClick={handleDownload} disabled={isDownloading}>
              {isDownloading ? 'Downloading…' : 'Download PDF'}
            </Button>
          )}
          <Button variant="primary" onClick={handlePrint} disabled={isDownloading}>
            Print
          </Button>
        </div>
      </div>
    </div>
  );
}
